import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";

const TABS = [
  { id: "impressum",           label: "Impressum" },
  { id: "datenschutz",         label: "Datenschutz" },
  { id: "nutzungsbedingungen", label: "Nutzungsbedingungen" },
  { id: "haftung",             label: "Haftungsausschluss" },
];

/* ---------- Inline primitives ---------- */
const H2 = ({ children }) => (
  <h2 className="font-outfit font-semibold text-xl text-white mb-2">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="font-outfit font-semibold text-[15px] text-white mt-5 mb-1.5">{children}</h3>
);
const P = ({ children }) => (
  <p className="text-[13.5px] text-white/65 leading-relaxed mb-2.5">{children}</p>
);
const Muted = ({ children }) => (
  <p className="text-[12px] text-white/35 leading-relaxed">{children}</p>
);
const K = ({ children }) => <strong className="text-white">{children}</strong>;

export default function LegalPage({ initialTab = "impressum" }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);

  const content = {
    /* ========================= IMPRESSUM ========================= */
    impressum: (
      <section className="space-y-2">
        <H2>Impressum</H2>
        <H3>Angaben gemäß § 5 TMG</H3>
        <P>
          <K>Bürokult GmbH</K><br />
          Espachstraße 13<br />
          88453 Erolzheim
        </P>

        <H3>Handelsregister</H3>
        <P>
          Registergericht: Amtsgericht Ulm<br />
          Registernummer: <K>HRB 746618</K>
        </P>

        <H3>Vertreten durch</H3>
        <P>Alexander Simon Baur</P>

        <H3>Kontakt</H3>
        <P>
          Telefon: <K>07354 / 94692-0</K><br />
          E-Mail: <K>info@buerokult.de</K> · Plattform-Kontakt: <K>baur@buerokult.de</K>
        </P>

        <H3>Umsatzsteuer-ID</H3>
        <P>Umsatzsteuer-Identifikationsnummer gemäß § 27 a UStG: <K>DE 362 926 060</K></P>

        <H3>Steuernummer</H3>
        <P>54003 / 44144</P>

        <H3>Streitschlichtung</H3>
        <P>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
          <a href="https://ec.europa.eu/consumers/odr" className="text-[#00e5ff] hover:underline" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>.
          Unsere E-Mail-Adresse findest du oben.<br />
          Wir sind nicht bereit oder verpflichtet, an Streitbeilegungs­verfahren vor einer Verbraucher­schlichtungs­stelle teilzunehmen.
        </P>

        <H3>Haftung für Inhalte</H3>
        <P>
          Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        </P>

        <div className="mt-5 border-t border-white/[0.06] pt-4">
          <Muted>
            <K>teamWERK Erolzheim</K> ist eine Marke der Bürokult GmbH.<br />
            [KI]NPUT ist eine Initiative von teamWERK Erolzheim, in Zusammenarbeit mit GLC² Coaching × Consulting (Georg Lamers).
          </Muted>
        </div>
      </section>
    ),

    /* ========================= DATENSCHUTZ ========================= */
    datenschutz: (
      <section className="space-y-2">
        <H2>Datenschutzerklärung</H2>

        <H3>1. Verantwortliche Stelle</H3>
        <P>
          <K>Bürokult GmbH</K>, Geschäftsführer Alexander Baur<br />
          Espachstraße 13, 88453 Erolzheim<br />
          Telefon: 07354 / 94692-0 · Datenschutz-Kontakt: <K>baur@buerokult.de</K>
        </P>

        <H3>2. Zweck der Plattform</H3>
        <P>
          [KI]NPUT ist eine nicht-öffentliche Community-Plattform. Eine Nutzung setzt Registrierung und Admin-Freischaltung voraus. Daten­verarbeitung erfolgt auf Basis von Art. 6 Abs. 1 lit. b DSGVO (Vertrag mit Mitgliedern), Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherer Bereitstellung) und Art. 6 Abs. 1 lit. a DSGVO (Einwilligung, z.B. Push-Benachrichtigungen).
        </P>

        <H3>3. Erhobene Daten</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li><K>Registrierung:</K> Vorname, Nachname, E-Mail, Passwort (gehasht mit bcrypt)</li>
          <li><K>Pflichtprofil:</K> Unternehmen, Position, KI-Level, KI-Interessen</li>
          <li><K>Optional:</K> Telefon, Avatar-Bild</li>
          <li><K>Nutzungsdaten:</K> Board-Beiträge, Kommentare, Event-Anmeldungen, Chat-Nachrichten (DMs + Gruppen), Tool-Bewertungen, Lernen-Fortschritt</li>
          <li><K>Zahlungsdaten:</K> Bei bezahlten Events — Rechnungs­anschrift; Kreditkarten­daten nicht bei uns, sondern direkt bei Stripe</li>
          <li><K>Authentifizierung:</K> verschlüsselte JWT-Session-Tokens (httpOnly-Cookie); bei aktiviertem 2FA: verschlüsseltes TOTP-Secret (AES-GCM)</li>
          <li><K>Sicherheit:</K> fehlgeschlagene Login-Versuche (15 Min), zuletzt-gesehen-Zeitpunkt (Aktivitätsmetrik)</li>
          <li><K>Server-Log:</K> IP, User-Agent, Zeitpunkt, Referrer — anonymisiert nach 7 Tagen</li>
        </ul>

        <H3>4. Hosting</H3>
        <P>
          Hosting in Deutschland bei einem professionellen Anbieter. Eine Übermittlung in Drittländer findet nur punktuell im Rahmen expliziter Dienste statt (siehe unten). Verträge zur Auftrags­verarbeitung (AV) liegen mit allen Dienstleistern vor.
        </P>

        <H3>5. E-Mail-Versand (SMTP)</H3>
        <P>
          Transaktionale E-Mails (Registrierungs­bestätigung, Freischaltung, Passwort-Reset, Event-Bestätigungen, Rechnungen) werden über einen deutschen Mailserver (KAS / all-inkl.com) SSL-verschlüsselt versandt. Kein Marketing­versand ohne ausdrückliche Einwilligung.
        </P>

        <H3>6. WebSocket-Chat &amp; DMs</H3>
        <P>
          Nachrichten werden auf unseren Servern gespeichert und sind nur für die Teilnehmer einer Konversation sowie den Administrator im Rahmen seiner Moderations­pflichten einsehbar. Transport­verschlüsselung via WSS. Dateianhänge bis 10 MB werden geprüft und clamen gespeichert.
        </P>

        <H3>7. Push-Benachrichtigungen (VAPID / Web-Push)</H3>
        <P>
          Nur nach ausdrücklicher Browser-Einwilligung. Wir speichern den vom Browser generierten Push-Endpoint und ein Public-Key-Paar (VAPID). Versendet werden die gleichen Benachrichtigungen wie auch als In-App-Meldung (neue DMs, Event-Erinnerungen). Abmeldung jederzeit in den Browser-Einstellungen oder im Profil.
        </P>

        <H3>8. Bezahlte Events &amp; Stripe</H3>
        <P>
          Bei kostenpflichtigen Veranstaltungen wickeln wir die Zahlung über <K>Stripe Payments Europe, Ltd.</K> ab (1 Grand Canal Street Lower, Dublin 2, Irland). Wir erhalten und speichern nur die Bestätigung (Session-ID, Betrag, Zeitpunkt). Zahlungs­mittel (Kreditkarten­nummer etc.) verarbeitet ausschließlich Stripe. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO. Details:{" "}
          <a href="https://stripe.com/de/privacy" className="text-[#00e5ff] hover:underline" target="_blank" rel="noopener noreferrer">stripe.com/de/privacy</a>.
        </P>

        <H3>9. KI-Services (OpenAI / Anthropic / Google Gemini)</H3>
        <P>
          Für Zusammen­fassungen von News-Artikeln, generierte Lehrmaterial-Bilder und künftige KI-Features nutzen wir die jeweiligen APIs der Anbieter — zentral über einen Meta-Dienst (Emergent). <K>Keine personen­bezogenen Daten</K> werden an diese Dienste übermittelt; nur öffentliche Artikel-Texte bzw. redaktionelle Prompts. Verantwortlichkeit nach Art. 28 DSGVO jeweils beim Anbieter.
        </P>

        <H3>10. Eingebettete Inhalte</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li><K>YouTube (erweiterter Datenschutz):</K> Die unter „News → Videos" eingebetteten Videos laufen über <K>youtube-nocookie.com</K>-Endpoints, Cookies werden erst beim Klick auf Play gesetzt. Anbieter: Google Ireland Ltd. Details:{" "}
            <a href="https://policies.google.com/privacy" className="text-[#00e5ff] hover:underline" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>.
          </li>
          <li><K>News-Crawler:</K> Texte, Bilder und Metadaten werden aus RSS-Feeds von t3n, all-ai.de, netzpolitik.org und anderen Quellen automatisch bezogen. Wir speichern nur Titel, Teaser, Link. Urheber­rechtliche Rechte verbleiben bei der jeweiligen Redaktion. <K>Keine Werbepartner­schaft.</K></li>
          <li><K>AI-Tool-Directory:</K> Metadaten werden automatisch gecrawlt. <K>Keine Provisionen, keine Affiliate-Links.</K></li>
        </ul>

        <H3>11. Cookies</H3>
        <P>
          Wir verwenden ausschließlich technisch notwendige Cookies (Authentifizierungs-Session, CSRF-Schutz). Keine Tracking-, Analyse- oder Marketing-Cookies. Rechts­grundlage: § 25 Abs. 2 Nr. 2 TTDSG.
        </P>

        <H3>12. Speicherdauer</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li>Account-Daten: bis zur Löschung durch Nutzer oder Admin</li>
          <li>Board-Beiträge: 10 Tage (automatische Löschung)</li>
          <li>Chat-Nachrichten: bis zur manuellen Löschung</li>
          <li>Rechnungs­daten: 10 Jahre (§ 147 AO)</li>
          <li>Fehl-Login-Einträge: 15 Minuten</li>
          <li>Server-Logs: 7 Tage, danach anonymisiert</li>
        </ul>

        <H3>13. Deine Rechte nach DSGVO</H3>
        <P>
          Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung (Art. 18), Daten­übertragbarkeit (Art. 20), Widerspruch (Art. 21) sowie Beschwerde bei der zuständigen Aufsichts­behörde. Kontakt wie oben. Einwilligungen (z.B. Push) sind jederzeit mit Wirkung für die Zukunft widerrufbar.
        </P>

        <H3>14. Datensicherheit</H3>
        <P>
          TLS/HTTPS überall. Passwörter bcrypt-gehasht (Cost 12). 2FA-Secrets AES-GCM-verschlüsselt. Admin-Accounts haben 2FA-Pflicht. Backups täglich, verschlüsselt. Zugriff auf Produktions­datenbanken nur durch benannte Personen mit 2FA.
        </P>

        <Muted>Stand: Februar 2026. Diese Datenschutzerklärung wurde auf den Funktions­umfang von [KI]NPUT zugeschnitten. Für finale juristische Prüfung wenden Sie sich an Ihren Anwalt.</Muted>
      </section>
    ),

    /* ========================= NUTZUNGSBEDINGUNGEN ========================= */
    nutzungsbedingungen: (
      <section className="space-y-2">
        <H2>Nutzungsbedingungen</H2>

        <H3>§ 1 Geltungsbereich</H3>
        <P>
          Diese Bedingungen regeln die Nutzung der Community-Plattform [KI]NPUT („Plattform"), betrieben von der <K>Bürokult GmbH</K>, Espachstraße 13, 88453 Erolzheim („wir", „Betreiber"), in Zusammenarbeit mit GLC² Coaching × Consulting (Georg Lamers). Die Plattform ist <K>nicht kommerziell</K>; sie dient dem Erfahrungs­austausch rund um Künstliche Intelligenz im Illertal, Oberschwaben und Allgäu.
        </P>

        <H3>§ 2 Registrierung und Freischaltung</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li>Die Registrierung ist freiwillig, kostenlos und erfordert eine Admin-Freischaltung.</li>
          <li>Vor der vollständigen Nutzung muss das Profil zu 100 % befüllt werden (Profile-Gate).</li>
          <li>Jede Person darf nur einen Account führen. Pflicht­angaben müssen wahrheits­gemäß sein.</li>
          <li>Ein Anspruch auf Freischaltung besteht nicht; wir können Anfragen ohne Angabe von Gründen ablehnen.</li>
        </ul>

        <H3>§ 3 Verhaltensregeln</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li>Jede:r Nutzer:in ist für eigene Inhalte (Beiträge, Kommentare, Chat, Dateien) selbst verantwortlich.</li>
          <li><K>Werbung</K> nur in Kategorie „Angebot". Erstkontakt und erster Austausch müssen kostenlos sein.</li>
          <li>Keine rechts­widrigen, diskriminierenden, belästigenden, gewalt­verherrlichenden, pornografischen, urheber­rechts­verletzenden oder personen­bezogen-verletzenden Inhalte.</li>
          <li>Kein Spam, keine automatisierten Massen­aktionen, kein Ausnutzen von Sicherheits­lücken.</li>
          <li>Verstöße führen zu Verwarnung, vorübergehender Sperre oder permanentem Ausschluss — nach Ermessen der Betreiber und ohne Entschädigungs­anspruch.</li>
        </ul>

        <H3>§ 4 Inhalte &amp; Rechte</H3>
        <P>
          Du behältst das Urheberrecht an deinen Beiträgen. Mit dem Posten räumst du uns ein einfaches, <K>nicht übertragbares, widerrufliches</K> Recht zur Anzeige auf der Plattform ein. Rechts­widrige Inhalte werden kommentarlos entfernt. Board-Beiträge verfallen automatisch nach 10 Tagen.
        </P>

        <H3>§ 5 Veranstaltungen</H3>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li>Events werden teils <K>kostenlos</K>, teils <K>kosten­pflichtig</K> angeboten. Der Preis wird vor der Anmeldung ausgewiesen.</li>
          <li>Bezahlung erfolgt ausschließlich über <K>Stripe</K>. Mit Klick auf „Bezahlen" kommt ein Vertrag zwischen dir und der Bürokult GmbH zustande.</li>
          <li><K>Widerrufsrecht</K>: Bei kostenpflichtigen Events gilt das 14-tägige Widerrufs­recht (§ 355 BGB), endet jedoch spätestens mit Beginn des Events (§ 356 Abs. 4 BGB). Bei Beginn innerhalb von 14 Tagen gilt dies mit deiner Zustimmung als Verzicht auf das Widerrufsrecht.</li>
          <li>Rechnung im PDF-Format wird per E-Mail versandt und ist zusätzlich im Profil herunterladbar.</li>
          <li>Max. 1 Gast pro Mitglied; Gast muss eine gültige E-Mail hinterlegen.</li>
          <li>No-Show: Bei kostenpflichtigen Events keine Rückerstattung.</li>
        </ul>

        <H3>§ 6 KI-Tools, News, Videos — Unabhängigkeit</H3>
        <P>
          Das AI-Tool-Directory wird automatisiert aus öffentlichen Quellen befüllt. News werden per RSS aus Fachredaktionen gezogen. YouTube-Videos stammen aus von uns kuratierten Kanälen. <K>Keine Werbepartner­schaften, keine Provisionen, keine bezahlten Platzierungen</K> — alle Empfehlungen sind redaktionell. Bewertungen stammen ausschließlich von Mitgliedern.
        </P>

        <H3>§ 7 KI-Lernen</H3>
        <P>
          Der „KI-Lernen"-Bereich ist ein Bildungs­angebot (<K>Basics</K>). Er wird laufend ausgebaut (Creative-AI, Vibe-Coding etc.). Inhalte ersetzen keine persönliche Beratung. Wir übernehmen keine Haftung für Entscheidungen, die du auf Grundlage dieses Materials triffst.
        </P>

        <H3>§ 8 Verfügbarkeit</H3>
        <P>
          Wir bemühen uns um hohe Verfügbarkeit, übernehmen aber keine Garantie für den ununter­brochenen Betrieb. Wartungs­fenster und Ausfälle sind möglich. Kein Anspruch auf Wieder­herstellung gelöschter Inhalte.
        </P>

        <H3>§ 9 Beendigung</H3>
        <P>
          Du kannst deinen Account jederzeit per Mail an <K>baur@buerokult.de</K> löschen lassen. Wir können Accounts aus wichtigem Grund (z.B. Verstoß gegen § 3) sperren oder löschen.
        </P>

        <H3>§ 10 Schlussbestimmungen</H3>
        <P>
          Es gilt deutsches Recht. Gerichtsstand ist, soweit gesetzlich zulässig, der Sitz der Bürokult GmbH (Erolzheim). Sollte eine Bestimmung unwirksam sein, bleiben die übrigen wirksam. Wir können diese Bedingungen anpassen; über wesentliche Änderungen informieren wir per E-Mail. Stand: <K>Februar 2026</K>.
        </P>
      </section>
    ),

    /* ========================= HAFTUNG ========================= */
    haftung: (
      <section className="space-y-2">
        <H2>Haftungsausschluss</H2>

        <H3>Plattform-Charakter</H3>
        <P>
          [KI]NPUT ist ein Marktplatz für Austausch. Die Bürokult GmbH verdient kein Geld mit der Plattform selbst (Umsätze entstehen ausschließlich über kostenpflichtige Event-Buchungen). Wenn Verträge oder Geschäfts­beziehungen zwischen Mitgliedern entstehen, sind die Betreiber daran nicht beteiligt.
        </P>

        <H3>Haftung für Inhalte</H3>
        <P>
          Wir übernehmen keine Haftung für
        </P>
        <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-white/65 mb-3 leading-relaxed">
          <li>von Nutzern erstellte Inhalte (Beiträge, Kommentare, Chat, Bewertungen)</li>
          <li>Richtigkeit geteilten Wissens, Tipps, Empfehlungen</li>
          <li>Qualität oder Leistung gelisteter KI-Tools</li>
          <li>Inhalte eingebetteter Drittanbieter (YouTube-Videos, News-Links)</li>
          <li>Geschäfts­beziehungen aus Kontakten über die Plattform</li>
          <li>Schäden jeglicher Art aus der Nutzung der Plattform, soweit gesetzlich zulässig</li>
        </ul>

        <H3>Haftung für Links</H3>
        <P>
          Die Plattform enthält Links zu externen Webseiten. Auf Inhalte dieser Drittseiten haben wir keinen Einfluss; für sie ist der jeweilige Anbieter verantwortlich. Zum Zeitpunkt der Verlinkung waren keine Rechtsverstöße erkennbar. Bei Kenntnis werden Links umgehend entfernt.
        </P>

        <H3>KI-Inhalte</H3>
        <P>
          Von KI (LLM) erzeugte Zusammen­fassungen können ungenau oder fehlerhaft sein („Halluzinationen"). Sie ersetzen keine eigenständige Recherche. Wir weisen bei allen KI-generierten Inhalten deutlich darauf hin.
        </P>

        <H3>Gesetzliche Haftung</H3>
        <P>
          Unberührt bleibt die gesetzliche Haftung bei Vorsatz, grober Fahrlässigkeit, Verletzung von Leben, Körper, Gesundheit sowie nach dem Produkt­haftungs­gesetz.
        </P>
      </section>
    ),
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {user ? <Navbar /> : (
        <div className="border-b border-white/[0.06] py-4 px-4 sm:px-6 bg-[#0a0a0a]">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/" className="font-outfit font-bold text-base tracking-tight">
              <span className="text-[#00e5ff]">[KI]</span><span className="text-white">NPUT</span>
            </Link>
            <Link to="/" className="flex items-center gap-1.5 text-sm text-white/30 hover:text-white/60 transition-colors">
              <ArrowLeft size={16} />Zurück
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <FileText size={20} className="text-white/30" />
          <h1 className="font-outfit font-bold text-2xl text-white tracking-tight" data-testid="legal-heading">Rechtliches</h1>
        </div>

        <div className="flex items-center gap-1 mb-6 border-b border-white/[0.06] pb-1 overflow-x-auto" data-testid="legal-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`legal-tab-${tab.id}`}
              className={`px-4 py-2 text-sm whitespace-nowrap font-outfit transition-colors ${
                activeTab === tab.id
                  ? "bg-white/10 text-white border-b border-[#00e5ff]"
                  : "text-white/30 hover:text-white/60"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="border border-white/[0.06] p-6 sm:p-8 bg-white/[0.015]" data-testid="legal-content">
          {content[activeTab]}
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-white/25">
            Powered by teamWERK Erolzheim · Bürokult GmbH · Espachstr. 13 · 88453 Erolzheim
          </p>
        </div>
      </div>
    </div>
  );
}
